import { FC, useState } from "react"

import { Box } from "@mui/material"
import { useQueryExportSchoolGroupsTask } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import {
  ExportButton,
  ExportMenu,
  ExportPendingBanner,
} from "components/common/exportMenu"
import {
  useExportGuard,
  useExportMenu,
  useExportTasks,
} from "hooks/exportReports"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { getErrorMessageFromTab } from "utils/api"

const ExportSchoolGroupsMenu: FC = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [isExportData, setIsExportData] = useState(false)

  const {
    successTasks,
    pendingTasks,
    refreshTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
    removeTask,
  } = useExportTasks({ exportType: ExportType.SCHOOL_GROUPS })

  const { isGenerateDisabled } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
  })

  const { isFetching } = useQueryExportSchoolGroupsTask({
    options: {
      enabled: isExportData,
      onSuccess: (response) => {
        setIsExportData(false)
        handleExportSuccess(response)
        showSnackbar({ title: t("export.fileIsCurrentlyBeingGenerated") })
      },
      onError: (error) => {
        setIsExportData(false)
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const {
    exportMenuAnchorEl,
    isExportMenuOpen,
    handleExportClick,
    handleExportMenuClose,
    handleGenerateNewFile,
  } = useExportMenu(isFetching, refreshTasks)

  return (
    <>
      <Box display="flex">
        <ExportButton isFetching={isFetching} onClick={handleExportClick} />
      </Box>
      <ExportMenu
        anchorEl={exportMenuAnchorEl}
        open={isExportMenuOpen}
        onClose={handleExportMenuClose}
        onGenerateNewFile={() =>
          handleGenerateNewFile(() => setIsExportData(true))
        }
        onFileClick={setExportReportIdToDownload}
        onFileRemove={removeTask}
        wrapFilename
        successTasks={successTasks}
        isGenerateDisabled={isGenerateDisabled}
        generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
      />
      <ExportPendingBanner visible={pendingTasks.length > 0} />
    </>
  )
}

export default ExportSchoolGroupsMenu
